import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, User, Building2, Receipt, Users, Settings, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isReseller = user?.role === 'reseller';
  const isAdmin = user?.role === 'admin';

  const resellerLinks = [
    { href: '/', label: 'Accounts', icon: Building2 },
    { href: '/billing', label: 'Billing', icon: Receipt },
  ];

  const adminLinks = [
    { href: '/admin', label: 'Requests', icon: Building2 },
    { href: '/admin/users', label: 'Users', icon: Users },
    { href: '/admin/billing', label: 'Billing', icon: Settings },
  ];

  const links = isReseller ? resellerLinks : isAdmin ? adminLinks : [];

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-white">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        <Link to={isAdmin ? '/admin' : '/'} className="flex items-center gap-3">
          <img src="/Abby_Logo.png" alt="Abby Connect" className="h-10" />
          <span className="text-lg font-semibold text-gray-900 hidden sm:inline">
            Reseller Portal
          </span>
        </Link>

        {isAuthenticated && user && (
          <div className="relative flex items-center" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-2 py-2 text-sm text-gray-700 hover:bg-gray-100 px-2 rounded-lg transition-colors"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-abby-primary/10 rounded-full">
                <User className="h-4 w-4 text-abby-primary" />
              </div>
              <span className="hidden sm:block max-w-[150px] truncate">{user.email}</span>
              <ChevronDown className={cn(
                "h-4 w-4 transition-transform",
                isMenuOpen && "rotate-180"
              )} />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border py-1 z-50">
                {/* User info */}
                <div className="px-4 py-3 border-b">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                  <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                </div>

                {/* Navigation links */}
                <div className="py-1">
                  {links.map((link) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.href ||
                      (link.href !== '/' && link.href !== '/admin' && location.pathname.startsWith(link.href));
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                          isActive
                            ? 'text-abby-primary bg-abby-primary/5'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>

                {/* Logout */}
                <div className="border-t py-1">
                  <button
                    onClick={logout}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
